// ── RiskAcceptanceCreationPage ─────────────────────────────────────────────────
//
// Route: /risk-acceptances/new  (create mode — no ?from=:id)
//        /risk-acceptances/new?from=:id  (edit mode — pre-filled from existing draft)
//
// §3.4 form pattern: native useState + canSubmit boolean per Phase A cluster pattern.
// No React Hook Form, no Formik, no Zod client-side.
//
// Role gate: risk_manager | super_admin (Submitter set per D-RA.3a-PE-8)
// §3.4 §10.2 §10.4 §10.8

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useAuthStore } from '@/store/auth.store';
import { useRiskAcceptance, useCreateRiskAcceptance, useUpdateDraftRiskAcceptance, useSubmitRiskAcceptance } from '@/api/hooks/useRiskAcceptances';
import { useVendors, useVendor } from '@/api/hooks/useVendors';
import { useDebounce } from '@/lib/hooks/useDebounce';
import { ResponseTypePicker } from '@/components/risk-acceptances/ResponseTypePicker';
import type { RiskResponseType } from '@/api/types/risk-acceptance.types';

// Role guard hook (cluster pattern from Phase A Q-RA.3b-PRE-2)
function useRequireRole(roles: string[]) {
  const navigate = useNavigate();
  const { isAuthenticated, isInitialized, hasAnyRole } = useAuthStore();
  useEffect(() => {
    if (isInitialized && isAuthenticated && !hasAnyRole(roles)) {
      navigate('/dashboard', { replace: true });
    }
  }, [isInitialized, isAuthenticated, hasAnyRole, navigate, roles]);
}

// ── Form field types ────────────────────────────────────────────────────────────

const RISK_SOURCES = [
  'vendor_assessment', 'compliance_assessment_finding', 'external_audit',
  'internal_audit', 'vuln_scan', 'pen_test', 'self_identified',
  'incident_derived', 'other',
] as const;

const RISK_SEVERITIES = ['low', 'medium', 'high', 'critical'] as const;

const RISK_CATEGORIES = [
  'vendor_risk', 'compliance_gap', 'vulnerability', 'operational_risk', 'other',
] as const;

// ── Date helper — TD-RA.3b-6 ──────────────────────────────────────────────────
//
// API may return date columns as ISO datetime strings (e.g. "2026-05-31T00:00:00Z").
// <input type="date"> requires YYYY-MM-DD. Slice to first 10 chars to normalize.

function toDateOnly(iso: string | null | undefined): string {
  if (!iso) return '';
  return iso.slice(0, 10); // YYYY-MM-DD from ISO datetime or plain date
}

// ── Component ─────────────────────────────────────────────────────────────────

export function RiskAcceptanceCreationPage() {
  useEffect(() => { document.title = 'New Risk Response — VeriGRC'; }, []);
  useRequireRole(['risk_manager', 'super_admin']);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromId = searchParams.get('from');
  const isEditMode = !!fromId;

  // Pre-fill from existing entry in edit mode
  const { data: existingEntry, isLoading: entryLoading } = useRiskAcceptance(fromId ?? undefined);

  // RA-FU-6: vendor typeahead — debounced search replacing static perPage:100 select
  const [vendorSearchText, setVendorSearchText] = useState('');
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);
  const debouncedVendorSearch = useDebounce(vendorSearchText, 300);

  // Edit mode: fetch vendor name by ID for disabled display
  const { data: editModeVendor } = useVendor(
    isEditMode && existingEntry?.vendor_id ? existingEntry.vendor_id : undefined,
  );

  // Typeahead search — enabled only in create mode with >= 1 char in search
  const { data: vendorSearchData } = useVendors({
    filters: { search: debouncedVendorSearch },
    perPage: 20,
    enabled: !isEditMode && debouncedVendorSearch.length >= 1,
  });
  const vendorSuggestions = vendorSearchData?.vendors ?? [];

  // Form state — native useState per cluster pattern
  const [vendorId, setVendorId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [riskCategory, setRiskCategory] = useState('');
  const [source, setSource] = useState('');
  const [sourceOtherSpecify, setSourceOtherSpecify] = useState('');
  const [potentialImpact, setPotentialImpact] = useState('');
  const [likelihood, setLikelihood] = useState('');
  const [impactSeverity, setImpactSeverity] = useState('');
  const [inherentRiskRating, setInherentRiskRating] = useState('');
  const [acceptanceStartDate, setAcceptanceStartDate] = useState('');
  const [acceptanceExpiresAt, setAcceptanceExpiresAt] = useState('');
  const [businessJustification, setBusinessJustification] = useState('');
  const [categoryOtherSpecify, setCategoryOtherSpecify] = useState('');
  const [inherentRiskOverride, setInherentRiskOverride] = useState(false);
  const [inherentRiskOverrideJustification, setInherentRiskOverrideJustification] = useState('');
  const [compensatingControls, setCompensatingControls] = useState('');
  // ⚠ SS2B-2b response-type axis. residual_risk_rating is no longer a standalone field — for Mitigate
  // the residual is reassessedResidualRating (the server backfills residual_risk_rating from it to satisfy
  // chk_ra_compensating_residual_required). Per-type state lives in distinct vars → naturally preserved
  // across type switches (Accept→Mitigate→Accept keeps the rationale).
  const [plannedResponseType, setPlannedResponseType] = useState<RiskResponseType>('accept');
  const [mitigateStage, setMitigateStage] = useState<'planned' | 'implemented'>('planned');
  const [responseRationale, setResponseRationale] = useState('');
  const [discontinuedScope, setDiscontinuedScope] = useState('');
  const [responsibleThirdParty, setResponsibleThirdParty] = useState('');
  const [reassessedResidualRating, setReassessedResidualRating] = useState('');
  const [longDurationJustification, setLongDurationJustification] = useState('');
  const [internalStakeholders, setInternalStakeholders] = useState('');
  const [externalEvidenceReferences, setExternalEvidenceReferences] = useState('');

  // fieldErrors state — cluster's first fieldErrors object envelope consumer (D-RA.3b-PE-4)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Submit warning modal state (§3.4 D-VA-RA-2)
  const [showSubmitWarning, setShowSubmitWarning] = useState(false);
  // pendingSubmitMode tracked for potential submit-path dispatch (AC-RA3b-18)
  const [_pendingSubmitMode, setPendingSubmitMode] = useState<'draft' | 'submit'>('draft');

  // Mutations
  const createMutation = useCreateRiskAcceptance();
  const updateMutation = useUpdateDraftRiskAcceptance();
  const submitMutation = useSubmitRiskAcceptance();

  const isPending = createMutation.isPending || updateMutation.isPending || submitMutation.isPending;

  // Pre-fill from existing entry (edit mode)
  useEffect(() => {
    if (existingEntry && isEditMode) {
      setVendorId(existingEntry.vendor_id ?? '');
      setTitle(existingEntry.title ?? '');
      setDescription(existingEntry.description ?? '');
      setRiskCategory(existingEntry.risk_category ?? '');
      setSource(existingEntry.source ?? '');
      setSourceOtherSpecify(existingEntry.source_other_specify ?? '');
      setPotentialImpact(existingEntry.potential_impact ?? '');
      setLikelihood(existingEntry.likelihood ?? '');
      setImpactSeverity(existingEntry.impact_severity ?? '');
      setInherentRiskRating(existingEntry.inherent_risk_rating ?? '');
      // TD-RA.3b-6: normalize to YYYY-MM-DD for <input type="date"> compatibility
      setAcceptanceStartDate(toDateOnly(existingEntry.acceptance_start_date));
      setAcceptanceExpiresAt(toDateOnly(existingEntry.acceptance_expires_at));
      setBusinessJustification(existingEntry.business_justification ?? '');
      setCategoryOtherSpecify(existingEntry.category_other_specify ?? '');
      setInherentRiskOverride(existingEntry.inherent_risk_override ?? false);
      setInherentRiskOverrideJustification(existingEntry.inherent_risk_override_justification ?? '');
      setCompensatingControls(existingEntry.compensating_controls ?? '');
      setPlannedResponseType((existingEntry.planned_response_type ?? 'accept') as RiskResponseType);
      setMitigateStage(existingEntry.implemented_response_type != null || existingEntry.response_implemented_at != null ? 'implemented' : 'planned');
      setResponseRationale(existingEntry.response_rationale ?? '');
      setDiscontinuedScope(existingEntry.discontinued_scope ?? '');
      setResponsibleThirdParty(existingEntry.responsible_third_party ?? '');
      setReassessedResidualRating(existingEntry.reassessed_residual_rating ?? '');
      setLongDurationJustification(existingEntry.long_duration_justification ?? '');
      setInternalStakeholders(existingEntry.internal_stakeholders ?? '');
      setExternalEvidenceReferences(existingEntry.external_evidence_references ?? '');
    }
  }, [existingEntry, isEditMode]);

  // §10.8 — conditional field rendering
  const showCategoryOther = riskCategory === 'other';
  const showSourceOther = source === 'other';
  const showOverrideJustification = inherentRiskOverride;

  // ⚠ SS2B-2b — the per-(type×phase) required-field matrix (mirrors the server validator, Decision C).
  // Accept → rationale + expiry; Avoid → discontinued scope; Share/Transfer → responsible third party;
  // Mitigate-IMPLEMENTED → compensating controls + reassessed residual (planned Mitigate requires nothing).
  const responseFieldsSatisfied =
    (plannedResponseType !== 'accept' || (responseRationale.trim().length > 0 && acceptanceExpiresAt !== '')) &&
    (plannedResponseType !== 'avoid' || discontinuedScope.trim().length > 0) &&
    ((plannedResponseType !== 'share' && plannedResponseType !== 'transfer') || responsibleThirdParty.trim().length > 0) &&
    (plannedResponseType !== 'mitigate' || mitigateStage !== 'implemented' ||
      (compensatingControls.trim().length > 0 && reassessedResidualRating !== ''));

  // canSubmit boolean per cluster pattern
  const canSubmit =
    vendorId.trim().length > 0 &&
    title.trim().length >= 10 &&
    description.trim().length >= 50 &&
    riskCategory !== '' &&
    source !== '' &&
    (!showSourceOther || sourceOtherSpecify.trim().length > 0) &&
    (!showCategoryOther || categoryOtherSpecify.trim().length > 0) &&
    potentialImpact.trim().length >= 30 &&
    likelihood !== '' &&
    impactSeverity !== '' &&
    inherentRiskRating !== '' &&
    acceptanceStartDate !== '' &&
    businessJustification.trim().length >= 100 &&
    (!showOverrideJustification || inherentRiskOverrideJustification.trim().length > 0) &&
    responseFieldsSatisfied;

  // §6.3 — error envelope detection
  function handleApiError(err: unknown): void {
    const axiosErr = err as { response?: { status?: number; data?: { errors?: unknown } } };
    const status = axiosErr?.response?.status;
    const errData = axiosErr?.response?.data?.errors;

    if (status === 400 && errData && !Array.isArray(errData) && typeof errData === 'object') {
      // fieldErrors object envelope → per-field errors
      const fe = errData as Record<string, string[]>;
      const mapped: Record<string, string> = {};
      for (const [key, msgs] of Object.entries(fe)) {
        if (Array.isArray(msgs) && msgs.length > 0) mapped[key] = msgs[0];
      }
      setFieldErrors(mapped);
    } else if (status === 400 && Array.isArray(errData)) {
      toast.error((errData as Array<{ message: string }>)[0]?.message ?? 'Validation error');
    } else if (status === 403) {
      toast.error("You don't have permission for this action");
    } else {
      toast.error('Something went wrong. Please try again.');
    }
  }

  // ⚠ SS2B-2b — the response-type payload slice. Only the ACTIVE type's fields are sent (others cleared);
  // implemented_response_type = the planned type once the Mitigate stage is Implemented (the server stamps
  // response_implemented_at + backfills residual_risk_rating from reassessed_residual_rating). `nullish`
  // is null on PATCH (edit) / undefined on POST (create) to match each payload's convention.
  function responseTypeSlice<N extends null | undefined>(nullish: N) {
    const isMit = plannedResponseType === 'mitigate';
    const implemented = isMit && mitigateStage === 'implemented';
    return {
      planned_response_type: plannedResponseType,
      implemented_response_type: implemented ? ('mitigate' as RiskResponseType) : nullish,
      response_rationale: plannedResponseType === 'accept' ? (responseRationale || nullish) : nullish,
      discontinued_scope: plannedResponseType === 'avoid' ? (discontinuedScope || nullish) : nullish,
      responsible_third_party:
        plannedResponseType === 'share' || plannedResponseType === 'transfer'
          ? (responsibleThirdParty || nullish)
          : nullish,
      compensating_controls: isMit ? (compensatingControls || nullish) : nullish,
      reassessed_residual_rating: isMit ? (reassessedResidualRating || nullish) : nullish,
    };
  }

  // §10.2 — submit handler dispatch
  async function handleSaveAsDraft() {
    setFieldErrors({});
    try {
      if (isEditMode && fromId) {
        // Edit mode: PATCH /:id
        const entry = await updateMutation.mutateAsync({
          id: fromId,
          payload: {
            title, description, risk_category: riskCategory, source,
            source_other_specify: showSourceOther ? sourceOtherSpecify : null,
            potential_impact: potentialImpact, likelihood, impact_severity: impactSeverity,
            inherent_risk_rating: inherentRiskRating, acceptance_start_date: acceptanceStartDate,
            acceptance_expires_at: acceptanceExpiresAt || null,
            business_justification: businessJustification,
            category_other_specify: showCategoryOther ? categoryOtherSpecify : null,
            inherent_risk_override: inherentRiskOverride,
            inherent_risk_override_justification: showOverrideJustification ? inherentRiskOverrideJustification : null,
            ...responseTypeSlice(null),
            long_duration_justification: longDurationJustification || null,
            internal_stakeholders: internalStakeholders || null,
            external_evidence_references: externalEvidenceReferences || null,
          },
        });
        toast.success('Draft saved');
        navigate(`/risk-acceptances/${entry.id}`);
      } else {
        // Create mode: POST / mode='draft'
        const { data: entry } = await createMutation.mutateAsync({
          mode: 'draft',
          vendor_id: vendorId, title, description, risk_category: riskCategory, source,
          source_other_specify: showSourceOther ? sourceOtherSpecify : undefined,
          potential_impact: potentialImpact, likelihood, impact_severity: impactSeverity,
          inherent_risk_rating: inherentRiskRating, acceptance_start_date: acceptanceStartDate,
          acceptance_expires_at: acceptanceExpiresAt || undefined,
          business_justification: businessJustification,
          category_other_specify: showCategoryOther ? categoryOtherSpecify : undefined,
          inherent_risk_override: inherentRiskOverride,
          inherent_risk_override_justification: showOverrideJustification ? inherentRiskOverrideJustification : undefined,
          ...responseTypeSlice(undefined),
          long_duration_justification: longDurationJustification || undefined,
          internal_stakeholders: internalStakeholders || undefined,
          external_evidence_references: externalEvidenceReferences || undefined,
        });
        toast.success('Draft saved');
        navigate(`/risk-acceptances/${entry.id}`);
      }
    } catch (err) {
      handleApiError(err);
    }
  }

  async function handleSubmitForApproval() {
    setFieldErrors({});
    try {
      if (isEditMode && fromId) {
        // Edit mode: POST /:id/submit (after optional PATCH to persist changes)
        // First save the current edits, then submit
        await updateMutation.mutateAsync({
          id: fromId,
          payload: {
            title, description, risk_category: riskCategory, source,
            source_other_specify: showSourceOther ? sourceOtherSpecify : null,
            potential_impact: potentialImpact, likelihood, impact_severity: impactSeverity,
            inherent_risk_rating: inherentRiskRating, acceptance_start_date: acceptanceStartDate,
            acceptance_expires_at: acceptanceExpiresAt || null,
            business_justification: businessJustification,
            category_other_specify: showCategoryOther ? categoryOtherSpecify : null,
            inherent_risk_override: inherentRiskOverride,
            inherent_risk_override_justification: showOverrideJustification ? inherentRiskOverrideJustification : null,
            ...responseTypeSlice(null),
            long_duration_justification: longDurationJustification || undefined,
            internal_stakeholders: internalStakeholders || undefined,
            external_evidence_references: externalEvidenceReferences || undefined,
          },
        });
        const entry = await submitMutation.mutateAsync(fromId);
        toast.success('Submitted for approval');
        navigate(`/risk-acceptances/${entry.id}`);
      } else {
        // Create mode: POST / mode='submit'
        const { data: entry } = await createMutation.mutateAsync({
          mode: 'submit',
          vendor_id: vendorId, title, description, risk_category: riskCategory, source,
          source_other_specify: showSourceOther ? sourceOtherSpecify : undefined,
          potential_impact: potentialImpact, likelihood, impact_severity: impactSeverity,
          inherent_risk_rating: inherentRiskRating, acceptance_start_date: acceptanceStartDate,
          acceptance_expires_at: acceptanceExpiresAt || undefined,
          business_justification: businessJustification,
          category_other_specify: showCategoryOther ? categoryOtherSpecify : undefined,
          inherent_risk_override: inherentRiskOverride,
          inherent_risk_override_justification: showOverrideJustification ? inherentRiskOverrideJustification : undefined,
          ...responseTypeSlice(undefined),
          long_duration_justification: longDurationJustification || undefined,
          internal_stakeholders: internalStakeholders || undefined,
          external_evidence_references: externalEvidenceReferences || undefined,
        });
        toast.success('Submitted for approval');
        navigate(`/risk-acceptances/${entry.id}`);
      }
    } catch (err) {
      handleApiError(err);
    }
  }

  if (isEditMode && entryLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 animate-pulse rounded bg-bg-surface-2" />
        <div className="h-64 animate-pulse rounded bg-bg-surface-2" />
      </div>
    );
  }

  const pageTitle = isEditMode ? 'Edit Risk Response Draft' : 'New Risk Response';

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">{pageTitle}</h1>
        <p className="mt-1 text-sm text-text-secondary">
          {isEditMode
            ? 'Update your draft before saving or submitting for approval.'
            : 'Submit a new vendor risk response request for Approver review.'}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Risk Response Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">

          {/* Vendor — RA-FU-6: typeahead search (replaces static select) */}
          <div className="space-y-1.5">
            <Label htmlFor="vendor-search">Vendor <span className="text-accent-rose">*</span></Label>
            {isEditMode ? (
              /* Edit mode: vendor_id is immutable; show name from lookup or fallback to ID */
              <Input
                id="vendor-search"
                value={editModeVendor?.name ?? vendorId}
                disabled
                className="opacity-50"
              />
            ) : (
              /* Create mode: live typeahead */
              <div className="relative">
                <Input
                  id="vendor-search"
                  value={vendorSearchText}
                  onChange={(e) => {
                    setVendorSearchText(e.target.value);
                    if (e.target.value === '') setVendorId('');
                    setVendorDropdownOpen(true);
                  }}
                  onFocus={() => setVendorDropdownOpen(true)}
                  onBlur={() => setTimeout(() => setVendorDropdownOpen(false), 150)}
                  placeholder="Search vendors…"
                  autoComplete="off"
                />
                {vendorDropdownOpen && vendorSuggestions.length > 0 && (
                  <ul
                    role="listbox"
                    aria-label="Vendor suggestions"
                    className="absolute z-10 mt-1 w-full rounded-md border border-border-subtle bg-bg-surface shadow-lg"
                  >
                    {vendorSuggestions.map((v) => (
                      <li
                        key={v.id}
                        role="option"
                        aria-selected={vendorId === v.id}
                        onMouseDown={() => {
                          setVendorId(v.id);
                          setVendorSearchText(v.name);
                          setVendorDropdownOpen(false);
                        }}
                        className="cursor-pointer px-3 py-2 text-sm text-text-primary hover:bg-bg-surface-2"
                      >
                        {v.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {fieldErrors.vendor_id && <p className="text-xs text-accent-rose">{fieldErrors.vendor_id}</p>}
          </div>

          {/* Title */}
          <div className="space-y-1.5">
            <Label htmlFor="title">Title <span className="text-accent-rose">*</span></Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief descriptive title (10–200 chars)"
              maxLength={200}
            />
            <p className="text-xs text-text-secondary">at least 10 characters ({title.length}/200)</p>
            {fieldErrors.title && <p className="text-xs text-accent-rose">{fieldErrors.title}</p>}
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label htmlFor="description">Description <span className="text-accent-rose">*</span></Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              maxLength={2000}
              placeholder="Detailed description of the risk (at least 50 characters)"
            />
            <p className="text-xs text-text-secondary">
              {description.trim().length < 50
                ? `at least 50 characters (${description.trim().length}/50)`
                : `${description.trim().length}/2000`}
            </p>
            {fieldErrors.description && <p className="text-xs text-accent-rose">{fieldErrors.description}</p>}
          </div>

          {/* Risk Category */}
          <div className="space-y-1.5">
            <Label htmlFor="risk-category">Risk Category <span className="text-accent-rose">*</span></Label>
            <select
              id="risk-category"
              value={riskCategory}
              onChange={(e) => { setRiskCategory(e.target.value); setCategoryOtherSpecify(''); }}
              className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan"
            >
              <option value="">Select category…</option>
              {RISK_CATEGORIES.map((c) => (
                <option key={c} value={c}>{c.replace(/_/g, ' ').replace(/\b\w/g, (s) => s.toUpperCase())}</option>
              ))}
            </select>
            {fieldErrors.risk_category && <p className="text-xs text-accent-rose">{fieldErrors.risk_category}</p>}
          </div>

          {/* §10.8 — category_other_specify (conditional) */}
          {showCategoryOther && (
            <div className="space-y-1.5">
              <Label htmlFor="category-other">Category — Other (specify) <span className="text-accent-rose">*</span></Label>
              <Input
                id="category-other"
                value={categoryOtherSpecify}
                onChange={(e) => setCategoryOtherSpecify(e.target.value)}
                placeholder="Describe the risk category"
              />
              {fieldErrors.category_other_specify && <p className="text-xs text-accent-rose">{fieldErrors.category_other_specify}</p>}
            </div>
          )}

          {/* Source */}
          <div className="space-y-1.5">
            <Label htmlFor="source">Risk Source <span className="text-accent-rose">*</span></Label>
            <select
              id="source"
              value={source}
              onChange={(e) => { setSource(e.target.value); setSourceOtherSpecify(''); }}
              className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan"
            >
              <option value="">Select source…</option>
              {RISK_SOURCES.map((s) => (
                <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</option>
              ))}
            </select>
            {fieldErrors.source && <p className="text-xs text-accent-rose">{fieldErrors.source}</p>}
          </div>

          {/* §10.8 — source_other_specify (conditional) */}
          {showSourceOther && (
            <div className="space-y-1.5">
              <Label htmlFor="source-other">Source — Other (specify) <span className="text-accent-rose">*</span></Label>
              <Input
                id="source-other"
                value={sourceOtherSpecify}
                onChange={(e) => setSourceOtherSpecify(e.target.value)}
                placeholder="Describe the risk source"
              />
              {fieldErrors.source_other_specify && <p className="text-xs text-accent-rose">{fieldErrors.source_other_specify}</p>}
            </div>
          )}

          {/* Potential Impact */}
          <div className="space-y-1.5">
            <Label htmlFor="potential-impact">Potential Impact <span className="text-accent-rose">*</span></Label>
            <Textarea
              id="potential-impact"
              value={potentialImpact}
              onChange={(e) => setPotentialImpact(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="Describe the potential business impact (at least 30 characters)"
            />
            <p className="text-xs text-text-secondary">
              {potentialImpact.trim().length < 30
                ? `at least 30 characters (${potentialImpact.trim().length}/30)`
                : `${potentialImpact.trim().length}/1000`}
            </p>
            {fieldErrors.potential_impact && <p className="text-xs text-accent-rose">{fieldErrors.potential_impact}</p>}
          </div>

          {/* Severity fields */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="likelihood">Likelihood <span className="text-accent-rose">*</span></Label>
              <select
                id="likelihood"
                value={likelihood}
                onChange={(e) => setLikelihood(e.target.value)}
                className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan"
              >
                <option value="">—</option>
                {RISK_SEVERITIES.map((s) => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
              {fieldErrors.likelihood && <p className="text-xs text-accent-rose">{fieldErrors.likelihood}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="impact-severity">Impact Severity <span className="text-accent-rose">*</span></Label>
              <select
                id="impact-severity"
                value={impactSeverity}
                onChange={(e) => setImpactSeverity(e.target.value)}
                className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan"
              >
                <option value="">—</option>
                {RISK_SEVERITIES.map((s) => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
              {fieldErrors.impact_severity && <p className="text-xs text-accent-rose">{fieldErrors.impact_severity}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="inherent-risk-rating">Inherent Risk Rating <span className="text-accent-rose">*</span></Label>
              <select
                id="inherent-risk-rating"
                value={inherentRiskRating}
                onChange={(e) => setInherentRiskRating(e.target.value)}
                className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan"
              >
                <option value="">—</option>
                {RISK_SEVERITIES.map((s) => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
              {fieldErrors.inherent_risk_rating && <p className="text-xs text-accent-rose">{fieldErrors.inherent_risk_rating}</p>}
            </div>
          </div>

          {/* Inherent Risk Override */}
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-sm text-text-primary cursor-pointer">
              <input
                type="checkbox"
                checked={inherentRiskOverride}
                onChange={(e) => { setInherentRiskOverride(e.target.checked); setInherentRiskOverrideJustification(''); }}
                className="h-4 w-4 rounded border-border-subtle"
              />
              Override inherent risk rating (requires justification)
            </label>
          </div>

          {/* §10.8 — inherent_risk_override_justification (conditional) */}
          {showOverrideJustification && (
            <div className="space-y-1.5">
              <Label htmlFor="override-justification">Override Justification <span className="text-accent-rose">*</span></Label>
              <Textarea
                id="override-justification"
                value={inherentRiskOverrideJustification}
                onChange={(e) => setInherentRiskOverrideJustification(e.target.value)}
                rows={3}
                placeholder="Explain why the inherent risk rating is being overridden"
              />
              {fieldErrors.inherent_risk_override_justification && (
                <p className="text-xs text-accent-rose">{fieldErrors.inherent_risk_override_justification}</p>
              )}
            </div>
          )}

          {/* ── Response strategy (SS2B-2b) — the picker determines which fields are required ── */}
          <div className="space-y-3 rounded-lg border border-border-subtle bg-bg-surface/40 p-4">
            <div className="space-y-1">
              <Label>Response Strategy <span className="text-accent-rose">*</span></Label>
              <p className="text-xs text-text-secondary">How this risk is being handled — the choice determines which fields become required.</p>
            </div>
            <ResponseTypePicker value={plannedResponseType} onChange={setPlannedResponseType} />

            {/* Accept → rationale (+ expiry, required below in Dates) */}
            {plannedResponseType === 'accept' && (
              <div className="space-y-1.5">
                <Label htmlFor="response-rationale">Rationale for accepting <span className="text-accent-rose">*</span></Label>
                <Textarea
                  id="response-rationale"
                  value={responseRationale}
                  onChange={(e) => setResponseRationale(e.target.value)}
                  rows={3}
                  placeholder="Why the residual exposure is tolerable, and what was weighed against it."
                />
                <p className="text-xs text-text-secondary">Accept also requires an expiry date (in Dates, below).</p>
                {fieldErrors.response_rationale && <p className="text-xs text-accent-rose">{fieldErrors.response_rationale}</p>}
              </div>
            )}

            {/* Avoid → discontinued scope */}
            {plannedResponseType === 'avoid' && (
              <div className="space-y-1.5">
                <Label htmlFor="discontinued-scope">Scope being discontinued <span className="text-accent-rose">*</span></Label>
                <Textarea
                  id="discontinued-scope"
                  value={discontinuedScope}
                  onChange={(e) => setDiscontinuedScope(e.target.value)}
                  rows={3}
                  placeholder="Which service, integration or data flow stops — precisely enough that someone can verify it later."
                />
                <p className="text-xs text-text-secondary">Avoid stays open until the discontinuation is confirmed — recording the intent is not the same as the scope being gone.</p>
                {fieldErrors.discontinued_scope && <p className="text-xs text-accent-rose">{fieldErrors.discontinued_scope}</p>}
              </div>
            )}

            {/* Share / Transfer → one shared block (only the note differs) */}
            {(plannedResponseType === 'share' || plannedResponseType === 'transfer') && (
              <div className="space-y-1.5">
                <Label htmlFor="responsible-third-party">Responsible third party <span className="text-accent-rose">*</span></Label>
                <Input
                  id="responsible-third-party"
                  value={responsibleThirdParty}
                  onChange={(e) => setResponsibleThirdParty(e.target.value)}
                  placeholder="Name who carries the risk"
                />
                <p className="text-xs text-text-secondary">
                  {plannedResponseType === 'share'
                    ? 'Share — the risk is carried jointly with the named third party.'
                    : 'Transfer — the risk moves wholly to the named third party.'}
                </p>
                {fieldErrors.responsible_third_party && <p className="text-xs text-accent-rose">{fieldErrors.responsible_third_party}</p>}
              </div>
            )}

            {/* Mitigate → stage + compensating controls + reassessed residual */}
            {plannedResponseType === 'mitigate' && (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Stage of this mitigation</Label>
                  <div className="flex flex-wrap gap-2">
                    {(['planned', 'implemented'] as const).map((st) => (
                      <button
                        key={st}
                        type="button"
                        onClick={() => setMitigateStage(st)}
                        className={`rounded-md border px-3 py-1.5 text-sm transition-colors ${
                          mitigateStage === st
                            ? 'border-accent-cyan bg-accent-cyan-dim/15 text-text-primary'
                            : 'border-border-subtle text-text-secondary hover:border-accent-cyan/40'
                        }`}
                      >
                        {st === 'planned' ? 'Planned' : 'Implemented'}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-text-secondary">
                    An implemented mitigation stays <strong className="text-text-primary">active</strong> — applying a compensating control is an implementation event, not a closure.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="compensating-controls">
                    Compensating controls {mitigateStage === 'implemented' && <span className="text-accent-rose">*</span>}
                  </Label>
                  <Textarea
                    id="compensating-controls"
                    value={compensatingControls}
                    onChange={(e) => setCompensatingControls(e.target.value)}
                    rows={3}
                    placeholder="What is being put in place, and how its effect will be evidenced."
                  />
                  {fieldErrors.compensating_controls && <p className="text-xs text-accent-rose">{fieldErrors.compensating_controls}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reassessed-residual">
                    Residual rating after the controls {mitigateStage === 'implemented'
                      ? <span className="text-accent-rose">*</span>
                      : <span className="text-xs font-normal text-text-secondary">(optional while planned)</span>}
                  </Label>
                  <select
                    id="reassessed-residual"
                    value={reassessedResidualRating}
                    onChange={(e) => setReassessedResidualRating(e.target.value)}
                    className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan"
                  >
                    <option value="">—</option>
                    {RISK_SEVERITIES.map((s) => (
                      <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                    ))}
                  </select>
                  {fieldErrors.reassessed_residual_rating && <p className="text-xs text-accent-rose">{fieldErrors.reassessed_residual_rating}</p>}
                </div>
              </div>
            )}
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="start-date">Start Date <span className="text-accent-rose">*</span></Label>
              <Input
                id="start-date"
                type="date"
                value={acceptanceStartDate}
                onChange={(e) => setAcceptanceStartDate(e.target.value)}
              />
              {fieldErrors.acceptance_start_date && <p className="text-xs text-accent-rose">{fieldErrors.acceptance_start_date}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="expires-at">Expiry Date</Label>
              <Input
                id="expires-at"
                type="date"
                value={acceptanceExpiresAt}
                onChange={(e) => setAcceptanceExpiresAt(e.target.value)}
              />
              {fieldErrors.acceptance_expires_at && <p className="text-xs text-accent-rose">{fieldErrors.acceptance_expires_at}</p>}
            </div>
          </div>

          {/* Business Justification */}
          <div className="space-y-1.5">
            <Label htmlFor="business-justification">Business Justification <span className="text-accent-rose">*</span></Label>
            <Textarea
              id="business-justification"
              value={businessJustification}
              onChange={(e) => setBusinessJustification(e.target.value)}
              rows={5}
              maxLength={3000}
              placeholder="Detailed justification for accepting this risk (at least 100 characters)"
            />
            <p className="text-xs text-text-secondary">
              {businessJustification.trim().length < 100
                ? `at least 100 characters (${businessJustification.trim().length}/100)`
                : `${businessJustification.trim().length}/3000`}
            </p>
            {fieldErrors.business_justification && <p className="text-xs text-accent-rose">{fieldErrors.business_justification}</p>}
          </div>

          {/* Optional fields */}
          <div className="space-y-1.5">
            <Label htmlFor="long-duration">Long Duration Justification</Label>
            <Textarea
              id="long-duration"
              value={longDurationJustification}
              onChange={(e) => setLongDurationJustification(e.target.value)}
              rows={2}
              placeholder="If the response period exceeds 12 months, explain why (optional)"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="stakeholders">Internal Stakeholders</Label>
            <Input
              id="stakeholders"
              value={internalStakeholders}
              onChange={(e) => setInternalStakeholders(e.target.value)}
              placeholder="Relevant internal stakeholders (optional)"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="evidence-refs">External Evidence References</Label>
            <Textarea
              id="evidence-refs"
              value={externalEvidenceReferences}
              onChange={(e) => setExternalEvidenceReferences(e.target.value)}
              rows={2}
              placeholder="Links or references to supporting evidence (optional)"
            />
          </div>

        </CardContent>
      </Card>

      {/* Action buttons */}
      <div className="flex items-center justify-end gap-3">
        <Button
          variant="outline"
          onClick={() => navigate(-1)}
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button
          variant="outline"
          onClick={handleSaveAsDraft}
          disabled={!canSubmit || isPending}
        >
          {(createMutation.isPending || updateMutation.isPending) && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          Save as Draft
        </Button>
        <Button
          onClick={() => {
            setPendingSubmitMode('submit');
            setShowSubmitWarning(true);
          }}
          disabled={!canSubmit || isPending}
        >
          {submitMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Submit for Approval
        </Button>
      </div>

      {/* Submit warning modal — D-VA-RA-2 */}
      {showSubmitWarning && (
        <Dialog open onOpenChange={(open) => { if (!open) setShowSubmitWarning(false); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-accent-amber" />
                Submit for Approval
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-text-secondary">
              You are submitting for approval. Once submitted, the entry cannot be edited
              until an Approver returns it for revision.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSubmitWarning(false)} disabled={isPending}>
                Go Back
              </Button>
              <Button
                onClick={async () => {
                  setShowSubmitWarning(false);
                  await handleSubmitForApproval();
                }}
                disabled={isPending}
              >
                {submitMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Confirm Submission
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
